'use client'

import { TrackFirstSignIn } from '@/feature/tracking/components/track-first-sign-in/TrackFirstSignIn.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { assertNever } from '@/lib/typescript/assertNever'
import type { IDidomiObject } from '@didomi/react'
import { DidomiSDK } from '@didomi/react'
import { GoogleTagManager } from '@next/third-parties/google'
import { useSetAtom } from 'jotai'
import { useCallback, useState } from 'react'
import type { AnyTracking, DataLayerEntry } from './tracking.eventTypes'
import { trackingConsentAtom } from './tracking.store'
import { type DidomiConsent } from '@/lib/didomi/hooks/useDidomiConsentSearchParam.client'

type TrackingManagerProps = {
  gtmId?: string
  didomiApiKey: string
  didomiNoticeId: string
  /** Set only when consent was already collected by mobile app */
  initialConsent?: DidomiConsent | undefined
}

function logError(message: string) {
  if (localStorage.getItem('trackingLogs') === 'true') {
    console.warn(`%c Tracking - ${message}`, 'color: #EF4444; font-weight: bold;')
  }
}

function logSuccess(message: string, data?: unknown) {
  if (localStorage.getItem('trackingLogs') === 'true') {
    console.warn(`%c Tracking - ${message}`, 'color: #22C55E; font-weight: bold;', data)
  }
}

/**
 * Checks if the user has given consent for Google Analytics tracking.
 */
function getDidUserGiveConsent(
  didomiInstance: IDidomiObject | null,
): { message: string; consent: false } | { message?: never; consent: true } {
  if (!didomiInstance) {
    logError('Didomi instance not available')

    return { message: 'Didomi instance not available', consent: false }
  }

  const vendors = didomiInstance.getCurrentUserStatus()?.vendors

  if (!vendors) {
    logError('no vendors info available')

    return { message: 'no vendors info available', consent: false }
  }

  if (!vendors.google?.enabled) {
    logError('no consent given for Google vendor')

    return { message: 'no consent given for Google vendor', consent: false }
  }

  const hasGoogleAnalyticsConsent = Object.keys(vendors).some(
    (vendorId) => vendorId.includes('googleana-') && vendors[vendorId]?.enabled === true,
  )

  if (!hasGoogleAnalyticsConsent) {
    logError('no consent given for Google Analytics')

    return { message: 'no consent given for Google Analytics', consent: false }
  }

  logSuccess('Consent for Google Analytics given')

  return { consent: true }
}

/**
 * Tracking Manager component to initialize Didomi and GTM
 */
export function TrackingManager({
  gtmId,
  didomiApiKey,
  didomiNoticeId,
  initialConsent,
}: TrackingManagerProps) {
  const [didomiInstance, setDidomiInstance] = useState<IDidomiObject | null>(null)

  const setTrackingConsent = useSetAtom(trackingConsentAtom)

  const handleDidomiReady = useCallback(
    (instance: IDidomiObject) => {
      setDidomiInstance(instance)

      if (initialConsent) {
        switch (initialConsent) {
          case 'granted':
            instance.setUserAgreeToAll()
            break
          case 'denied':
            instance.setUserDisagreeToAll()
            break
          default:
            assertNever(initialConsent)
        }
      }

      const { consent, message } = getDidUserGiveConsent(window.Didomi)
      setTrackingConsent(consent)

      if (message) {
        logError(message)
      }
    },
    [initialConsent, setTrackingConsent],
  )

  const isDidomiReady = !!didomiInstance

  return (
    <>
      <DidomiSDK
        apiKey={didomiApiKey}
        noticeId={didomiNoticeId}
        // Notice must be disabled at config level when initial consent is given on mobile app (otherwise it will be displayed on webview)
        config={initialConsent ? { notice: { enable: false } } : undefined}
        onReady={handleDidomiReady}
        onConsentChanged={() => {
          const { consent, message } = getDidUserGiveConsent(window.Didomi)
          setTrackingConsent(consent)

          if (message) {
            logError(message)
          }
        }}
      />
      {isDidomiReady && gtmId && (
        <>
          <GoogleTagManager gtmId={gtmId} />
          <TrackFirstSignIn />
        </>
      )}
    </>
  )
}

/**
 * Confirmed with Julien G. on Dec. 17 2025:
 * "It is OK to feed GTM dataLayer even user did NOT give consent.
 * It is the use of these data in GA that is blocked until consent is given."
 */
export function pushToGtmDataLayer(eventData: AnyTracking) {
  if (isSSR) {
    console.error('pushToGtmDataLayer called on SSR')

    return
  }

  const dataLayer = window.dataLayer as DataLayerEntry[]

  if (!dataLayer) {
    logError('dataLayer is not defined on window object')

    return
  }

  if (process.env.NODE_ENV === 'production') {
    dataLayer.push(eventData)
  } else {
    logSuccess(`Pushing event "${eventData.event}" to dataLayer`, eventData)
  }
}
